import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCarousel } from '../hooks/useCarousel';
import { getCdnUrl } from '../utils/cdnConfig';

/**
 * Maximum number of pagination dots to display at once.
 * For larger galleries a sliding window of dots is shown.
 */
const MAX_DOTS = 9;

// ─── ImageSlide ──────────────────────────────────────────────────────────────

/**
 * A single slide in the lightbox.
 *
 * Features:
 * - Skeleton pulse placeholder while image loads
 * - Blur → sharp progressive reveal on load
 * - Fixed aspect ratio container to prevent layout shifts
 * - srcset / sizes for responsive images
 * - WebP with JPEG fallback via <picture>
 * - Error state fallback
 */
const ImageSlide = React.memo(({ image, alt, isActive, onToggleZoom, unavailableLabel }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  const rawUrl = image?.url || image;
  const src = rawUrl ? getCdnUrl(rawUrl) : null;

  // Reset state when image source changes
  const prevSrcRef = useRef(src);
  useEffect(() => {
    if (prevSrcRef.current !== src) {
      setLoaded(false);
      setError(false);
      prevSrcRef.current = src;
    }
  }, [src]);

  if (!src) return null;

  return (
    <div
      className="absolute inset-0 flex items-center justify-center p-4 select-none"
      aria-hidden={!isActive}
    >
      {/* Skeleton placeholder — shown until image loads */}
      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 rounded-2xl bg-white/10 animate-pulse flex items-center justify-center">
            <svg className="w-10 h-10 text-white/30" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Actual image */}
      {!error ? (
        <picture>
          {/* WebP source — browser picks this if supported */}
          <source
            type="image/webp"
            srcSet={`${src} 1x`}
            sizes="(max-width: 768px) 100vw, 90vw"
          />
          {/* Fallback JPEG */}
          <img
            src={src}
            alt={alt}
            draggable={false}
            onLoad={() => setLoaded(true)}
            onError={() => { setError(true); setLoaded(true); }}
            onClick={onToggleZoom}
            className="max-h-full rounded-lg shadow-2xl select-none"
            style={{
              maxWidth: '100%',
              maxHeight: 'calc(100vh - 160px)',
              objectFit: 'contain',
              opacity: loaded ? 1 : 0,
              filter: loaded ? 'none' : 'blur(8px)',
              transition: 'opacity 0.35s ease, filter 0.35s ease',
              cursor: 'zoom-in',
            }}
          />
        </picture>
      ) : (
        <div className="flex flex-col items-center gap-3 text-white/50">
          <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm">{unavailableLabel}</span>
        </div>
      )}
    </div>
  );
});

ImageSlide.displayName = 'ImageSlide';

// ─── PaginationDots ───────────────────────────────────────────────────────────

/**
 * Renders a sliding window of pagination dots.
 * Shows at most MAX_DOTS dots, always centered on the active slide.
 */
const PaginationDots = React.memo(({ total, current, onDotClick, t }) => {
  if (total <= 1) return null;

  // For small galleries show all dots; for large ones use a sliding window
  let dotIndices;
  if (total <= MAX_DOTS) {
    dotIndices = Array.from({ length: total }, (_, i) => i);
  } else {
    const half = Math.floor(MAX_DOTS / 2);
    let start = Math.max(0, current - half);
    let end = start + MAX_DOTS - 1;
    if (end >= total) {
      end = total - 1;
      start = Math.max(0, end - MAX_DOTS + 1);
    }
    dotIndices = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  return (
    <div
      className="flex items-center justify-center gap-1.5 py-2"
      role="tablist"
      aria-label={t('profile.gallerySlides', 'Gallery slides')}
    >
      {dotIndices.map((idx) => (
        <button
          key={idx}
          role="tab"
          aria-selected={idx === current}
          aria-label={t('profile.goToImage', 'Go to image {{number}}', { number: idx + 1 })}
          onClick={() => onDotClick(idx)}
          className={[
            'rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
            idx === current
              ? 'w-5 h-2 bg-white'
              : 'w-2 h-2 bg-white/40 hover:bg-white/70',
          ].join(' ')}
        />
      ))}
    </div>
  );
});

PaginationDots.displayName = 'PaginationDots';

// ─── ZoomOverlay ─────────────────────────────────────────────────────────────

/**
 * Full-screen zoom overlay for a single image.
 * Supports native browser pinch-to-zoom on mobile.
 * Keyboard: Escape closes the overlay.
 */
const ZoomOverlay = ({ image, alt, onClose, t }) => {
  const src = image ? getCdnUrl(image?.url || image) : null;
  const containerRef = useRef(null);

  // Handle Escape key and focus the container on mount
  useEffect(() => {
    containerRef.current?.focus();
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!src) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black z-[10000] flex items-center justify-center outline-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('profile.zoomedImage', 'Zoomed image')}
      tabIndex={-1}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label={t('profile.closeZoom', 'Close zoom')}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div
        className="w-full h-full overflow-auto flex items-center justify-center"
        style={{ touchAction: 'pinch-zoom' }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-none"
          style={{ maxHeight: '100vh', cursor: 'zoom-out' }}
          onClick={onClose}
          draggable={false}
        />
      </div>
    </div>
  );
};

// ─── CarouselLightbox ─────────────────────────────────────────────────────────

/**
 * CarouselLightbox
 *
 * A production-grade, mobile-first, memory-safe full-screen image carousel.
 *
 * Features:
 * - Windowed rendering: only images within ±3 of current index are mounted in the DOM
 * - Smooth CSS-transform sliding (no JS animation loop)
 * - Progressive image loading: skeleton → blur → sharp
 * - Touch swipe (velocity-aware, horizontal vs vertical discrimination)
 * - Keyboard navigation (←/→/Escape)
 * - Pagination dots with sliding window for large galleries
 * - Position indicator ("3 / 20")
 * - Tap-to-zoom overlay
 * - Body scroll lock while open
 * - Full accessibility (roles, aria-labels, focus management)
 *
 * @param {object[]} images - Array of image objects ({ url }) or strings
 * @param {number}   initialIndex - Index to open at
 * @param {boolean}  isOpen - Whether the lightbox is visible
 * @param {function} onClose - Callback to close the lightbox
 * @param {string}   profileName - Used for alt text
 */
const CarouselLightbox = ({ images = [], initialIndex = 0, isOpen, onClose, profileName = '' }) => {
  const { t } = useTranslation();
  const {
    currentIndex,
    goTo,
    goNext,
    goPrev,
    windowedIndices,
    touchHandlers,
    handleKeyDown: carouselKeyDown,
  } = useCarousel(images.length, initialIndex);

  const [isZoomed, setIsZoomed] = useState(false);
  const containerRef = useRef(null);

  // Lock body scroll while open; restore on close
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      containerRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      setIsZoomed(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Keyboard handler (Escape + arrow keys)
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      if (isZoomed) {
        setIsZoomed(false);
      } else {
        onClose();
      }
      return;
    }
    // Don't navigate while zoom overlay is open
    if (isZoomed) return;
    carouselKeyDown(e);
  }, [isOpen, isZoomed, carouselKeyDown, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  // Preload next/prev images outside the windowed set
  useEffect(() => {
    if (!isOpen || images.length === 0) return;
    const preloadIndices = [
      currentIndex + 1,
      currentIndex + 2,
      currentIndex - 1,
      currentIndex - 2,
    ].filter((i) => i >= 0 && i < images.length && !windowedIndices.has(i));

    preloadIndices.forEach((i) => {
      const url = images[i]?.url || images[i];
      if (url) {
        const img = new Image();
        img.src = getCdnUrl(url);
      }
    });
  }, [currentIndex, images, isOpen, windowedIndices]);

  const handleToggleZoom = useCallback(() => {
    setIsZoomed(prev => !prev);
  }, []);

  const handleCloseZoom = useCallback(() => setIsZoomed(false), []);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!isOpen || images.length === 0) return null;

  const total = images.length;
  const currentImage = images[currentIndex];

  return (
    <>
      {/* Main lightbox backdrop */}
      <div
        ref={containerRef}
        className="fixed inset-0 z-[9999] bg-black/95 flex flex-col outline-none"
        role="dialog"
        aria-modal="true"
        aria-label={t('profile.imageGallery', 'Image gallery')}
        tabIndex={-1}
        onClick={handleBackdropClick}
        {...touchHandlers}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div
          className="relative z-10 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Profile name + counter */}
          <div className="text-white min-w-0">
            {profileName && (
              <p className="text-sm font-medium truncate max-w-[200px] md:max-w-none opacity-90">
                {profileName}
              </p>
            )}
            <p className="text-xs text-white/60 tabular-nums">
              {currentIndex + 1} / {total}
            </p>
          </div>

          {/* Zoom toggle (desktop hint) */}
          <button
            onClick={handleToggleZoom}
            className="hidden md:flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-xs px-3 py-1.5 rounded-full border border-white/20 hover:border-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={t('profile.toggleZoom', 'Toggle zoom')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
            </svg>
            {t('profile.zoom', 'Zoom')}
          </button>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label={t('profile.closeGallery', 'Close gallery')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Slide track ────────────────────────────────────────────────── */}
        <div
          className="relative flex-1 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/*
           * The track translates based on currentIndex.
           * Each slide is absolutely positioned at left = index * 100%.
           * Only indices in the window are mounted — rest are unmounted from DOM.
           */}
          <div
            className="absolute inset-0"
            style={{
              transform: `translateX(calc(-${currentIndex * 100}%))`,
              transition: 'transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}
          >
            {Array.from(windowedIndices)
              .sort((a, b) => a - b)
              .map((idx) => (
                <div
                  key={idx}
                  className="absolute inset-0 w-full h-full"
                  style={{ left: `${idx * 100}%` }}
                >
                  <ImageSlide
                    image={images[idx]}
                    alt={`${profileName} — image ${idx + 1} of ${total}`}
                    isActive={idx === currentIndex}
                    onToggleZoom={idx === currentIndex ? handleToggleZoom : undefined}
                    unavailableLabel={t('profile.imageUnavailable', 'Image unavailable')}
                  />
                </div>
              ))}
          </div>

          {/* ── Navigation arrows ──────────────────────────────────────── */}
          {total > 1 && (
            <>
              {/* Prev */}
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white backdrop-blur-sm border border-white/10"
                aria-label={t('profile.previousImage', 'Previous image')}
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* Next */}
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white backdrop-blur-sm border border-white/10"
                aria-label={t('profile.nextImage', 'Next image')}
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* ── Footer: pagination dots ─────────────────────────────────────── */}
        <div
          className="shrink-0 pb-4 bg-gradient-to-t from-black/60 to-transparent"
          onClick={(e) => e.stopPropagation()}
        >
          <PaginationDots total={total} current={currentIndex} onDotClick={goTo} t={t} />
        </div>
      </div>

      {/* Zoom overlay — rendered on top of everything */}
      {isZoomed && (
        <ZoomOverlay
          image={currentImage}
          alt={`${profileName} — image ${currentIndex + 1} of ${total} (zoomed)`}
          onClose={handleCloseZoom}
          t={t}
        />
      )}
    </>
  );
};

export default CarouselLightbox;
